import { useState, useEffect, useRef, useCallback } from "react";

// ═══════════════════════════════════════════
// LURE LINK — Premium Fishing Lure Marketplace
// ═══════════════════════════════════════════

const COLORS = {
    bg: "#0a0f1a",
    bgCard: "#111827",
    bgCardHover: "#1a2438",
    surface: "#1e293b",
    border: "#2a3a52",
    accent: "#00e5a0",
    accentDim: "#00c48a",
    accentGlow: "rgba(0,229,160,0.15)",
    gold: "#f5c542",
    goldDim: "#d4a535",
    text: "#e8edf5",
    textMuted: "#8899b0",
    textDim: "#5a6b82",
    danger: "#ff4d6a",
    water: "#1a3a5c",
    waterLight: "#2a5a8c",
};

const FONTS = {
    display: "'Playfair Display', Georgia, serif",
    body: "'DM Sans', 'Segoe UI', sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', monospace",
};

// ═══════════════════════════════════════════
// SVG ICONS (no emojis)
// ═══════════════════════════════════════════

function Icon({ name, size = 18, color = "currentColor", style = {} }) {
    const icons = {
        hook: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M12 2v8" /><path d="M8 6l4-4 4 4" /><path d="M12 10c0 4-4 6-4 10a4 4 0 0 0 8 0c0-4-4-6-4-10z" /></svg>,
        search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
        target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
        cart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>,
        flame: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M12 2c0 4-4 6-4 10a4 4 0 0 0 8 0c0-4-4-6-4-10z" /></svg>,
        palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="13.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="10.5" r="2.5" fill="none" /><circle cx="8.5" cy="7.5" r="2.5" fill="none" /><circle cx="6.5" cy="12.5" r="2.5" fill="none" /><path d="M12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10-1.5 5-3 5h-2a2 2 0 0 0-2 2c0 1.5.5 3-3 3z" /></svg>,
        clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" /></svg>,
        dollar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
        check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22,4 12,14.01 9,11.01" /></svg>,
        edit: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
        send: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22,2 15,22 11,13 2,9" /></svg>,
        chat: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
        star: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" /></svg>,
        camera: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>,
        pin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
        award: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="12" cy="8" r="7" /><polyline points="8.21,13.89 7,23 12,20 17,23 15.79,13.88" /></svg>,
        zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><polygon points="13,2 3,14 12,14 11,22 21,10 12,10" /></svg>,
        sparkle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" /></svg>,
        fish: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M6.5 12c3-6 10-6 14-2-4 4-11 4-14-2z" /><path d="M6.5 12c-3-2-4-5-4-5s1 3 0 5c-1 2 0 5 0 5s1-3 4-5z" /><circle cx="16" cy="11" r="1" fill={color} /></svg>,
        bot: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="3" y="8" width="18" height="12" rx="2" /><path d="M12 2v6" /><circle cx="9" cy="14" r="1.5" fill={color} /><circle cx="15" cy="14" r="1.5" fill={color} /></svg>,
        lure: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M12 2v4" /><circle cx="12" cy="8" r="2" /><path d="M12 10v2" /><ellipse cx="12" cy="16" rx="4" ry="5" /><path d="M8 18c0 2 1.5 4 4 4s4-2 4-4" /></svg>,
    };
    return icons[name] || null;
}

// Mock Data
const LURE_TYPES = ["Crankbait", "Spinnerbait", "Jig", "Topwater", "Swimbait", "Soft Plastic", "Spoon", "Fly"];
const FISH_TARGETS = ["Bass", "Trout", "Walleye", "Pike", "Musky", "Panfish", "Catfish", "Salmon"];
const WATER_CONDITIONS = ["Clear", "Murky", "Stained", "Deep", "Shallow", "Weedy", "Rocky", "Current"];

const MOCK_ARTISTS = [
    { id: 1, name: "Jake Thornton", handle: "@thorntonlures", location: "Ozark, MO", rating: 4.9, reviews: 234, sales: 1820, specialty: "Hand-painted Crankbaits", bio: "Third-generation lure maker. Every bait is hand-carved from basswood and finished with 12+ coats.", avatar: "JT", verified: true, joined: "2019", responseTime: "< 2 hrs" },
    { id: 2, name: "Maria Santos", handle: "@santosbaits", location: "Lake Fork, TX", rating: 4.8, reviews: 189, sales: 1450, specialty: "Custom Swimbaits", bio: "Former marine biologist turned lure artist. My swimbaits move like the real thing.", avatar: "MS", verified: true, joined: "2020", responseTime: "< 4 hrs" },
    { id: 3, name: "Dale Hutchins", handle: "@hutchcrafted", location: "Brainerd, MN", rating: 5.0, reviews: 312, sales: 2100, specialty: "Musky Bucktails", bio: "50 years on the water. Hand-tied bucktails that have landed state records.", avatar: "DH", verified: true, joined: "2018", responseTime: "< 1 hr" },
    { id: 4, name: "Aiko Yamada", handle: "@aikocustoms", location: "Portland, OR", rating: 4.9, reviews: 156, sales: 980, specialty: "Artisan Fly Lures", bio: "Blending Japanese craft traditions with Pacific Northwest fly patterns.", avatar: "AY", verified: true, joined: "2021", responseTime: "< 3 hrs" },
    { id: 5, name: "Bobby Ray Clemens", handle: "@bigbobslures", location: "Guntersville, AL", rating: 4.7, reviews: 445, sales: 3200, specialty: "Tournament Jigs", bio: "20+ tournament wins. My jigs are designed to flip, pitch, and catch giants.", avatar: "BC", verified: true, joined: "2017", responseTime: "< 6 hrs" },
];

const MOCK_LURES = [
    { id: 1, name: "Bluegill Crankbait Pro", artist: MOCK_ARTISTS[0], price: 28, type: "Crankbait", target: "Bass", water: "Clear", iconName: "fish", colors: ["Natural Bluegill", "Ghost Shad", "Chartreuse"], depth: "4-8ft", inStock: true, rating: 4.9, sold: 340 },
    { id: 2, name: "Midnight Musky Bucktail", artist: MOCK_ARTISTS[2], price: 45, type: "Spinnerbait", target: "Musky", water: "Stained", iconName: "lure", colors: ["Black/Gold", "Fire Tiger", "Purple Haze"], depth: "6-15ft", inStock: true, rating: 5.0, sold: 210 },
    { id: 3, name: "Sunrise Swimbait 6\"", artist: MOCK_ARTISTS[1], price: 65, type: "Swimbait", target: "Bass", water: "Clear", iconName: "hook", colors: ["Rainbow Trout", "Baby Bass", "Hitch"], depth: "2-12ft", inStock: true, rating: 4.8, sold: 180 },
    { id: 4, name: "Royal Coachman Fly", artist: MOCK_ARTISTS[3], price: 12, type: "Fly", target: "Trout", water: "Current", iconName: "fish", colors: ["Traditional", "UV Enhanced"], depth: "Surface", inStock: true, rating: 4.9, sold: 520 },
    { id: 5, name: "Punching Jig 1oz", artist: MOCK_ARTISTS[4], price: 8, type: "Jig", target: "Bass", water: "Weedy", iconName: "zap", colors: ["PB&J", "Black/Blue", "Green Pumpkin"], depth: "Heavy Cover", inStock: true, rating: 4.7, sold: 890 },
    { id: 6, name: "Ghost Shad Topwater", artist: MOCK_ARTISTS[0], price: 32, type: "Topwater", target: "Bass", water: "Shallow", iconName: "lure", colors: ["Bone", "Chrome", "Sexy Shad"], depth: "Surface", inStock: false, rating: 4.8, sold: 275 },
    { id: 7, name: "Perch Glide Bait", artist: MOCK_ARTISTS[1], price: 85, type: "Swimbait", target: "Pike", water: "Clear", iconName: "fish", colors: ["Natural Perch", "Albino", "Fire Perch"], depth: "4-10ft", inStock: true, rating: 4.9, sold: 95 },
    { id: 8, name: "Walleye Crawler Harness", artist: MOCK_ARTISTS[2], price: 15, type: "Spinnerbait", target: "Walleye", water: "Deep", iconName: "hook", colors: ["Chartreuse/Orange", "Pink/White", "Copper"], depth: "15-30ft", inStock: true, rating: 4.6, sold: 670 },
];

const MOCK_AUCTIONS = [
    { id: 1, name: "One-of-a-Kind Largemouth Swimbait", artist: MOCK_ARTISTS[1], currentBid: 340, bids: 23, timeLeft: "2h 14m", iconName: "award", description: "Hand-painted museum-quality 8\" jointed swimbait. Real fish scale pattern under 20 coats of clear.", reserve: 200, startPrice: 100 },
    { id: 2, name: "Custom Musky Bucktail Set (5pc)", artist: MOCK_ARTISTS[2], currentBid: 580, bids: 41, timeLeft: "45m", iconName: "flame", description: "Complete set of 5 hand-tied bucktails. Each one unique, numbered, and signed.", reserve: 300, startPrice: 150 },
    { id: 3, name: "Artisan Fly Collection - 12 Flies", artist: MOCK_ARTISTS[3], currentBid: 125, bids: 15, timeLeft: "5h 30m", iconName: "sparkle", description: "12 unique hand-tied flies featuring rare materials and traditional Japanese techniques.", reserve: 80, startPrice: 50 },
    { id: 4, name: "Tournament-Proven Jig Kit (20pc)", artist: MOCK_ARTISTS[4], currentBid: 210, bids: 31, timeLeft: "1h 05m", iconName: "zap", description: "The exact jigs that won Guntersville Open. 20 hand-poured, hand-tied jigs.", reserve: 100, startPrice: 75 },
];

// ═══════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════

function GlowText({ children, color = COLORS.accent, style = {} }) {
    return (
        <span style={{ color, textShadow: `0 0 20px ${color}44, 0 0 40px ${color}22`, ...style }}>
            {children}
        </span>
    );
}

function Badge({ children, color = COLORS.accent, style = {} }) {
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: `${color}18`, color, border: `1px solid ${color}33`,
            fontFamily: FONTS.body, letterSpacing: 0.3, ...style,
        }}>{children}</span>
    );
}

function StarRating({ rating, size = 13 }) {
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            {[1, 2, 3, 4, 5].map(i => (
                <span key={i} style={{ color: i <= Math.round(rating) ? COLORS.gold : COLORS.textDim, fontSize: size, lineHeight: 1 }}>
                    {String.fromCharCode(9733)}
                </span>
            ))}
            <span style={{ color: COLORS.textMuted, fontSize: size - 1, marginLeft: 4, fontFamily: FONTS.body }}>{rating}</span>
        </span>
    );
}

function Button({ children, variant = "primary", onClick, style = {}, disabled = false }) {
    const [hov, setHov] = useState(false);
    const base = {
        padding: "10px 22px", borderRadius: 10, fontFamily: FONTS.body, fontWeight: 600,
        fontSize: 14, cursor: disabled ? "not-allowed" : "pointer", border: "none",
        transition: "all 0.25s ease", display: "inline-flex", alignItems: "center", gap: 8,
        opacity: disabled ? 0.5 : 1,
    };
    const variants = {
        primary: { background: hov ? COLORS.accentDim : COLORS.accent, color: COLORS.bg, boxShadow: hov ? `0 4px 20px ${COLORS.accent}44` : "none" },
        secondary: { background: hov ? COLORS.surface : "transparent", color: COLORS.text, border: `1px solid ${COLORS.border}` },
        gold: { background: hov ? COLORS.goldDim : COLORS.gold, color: COLORS.bg, boxShadow: hov ? `0 4px 20px ${COLORS.gold}44` : "none" },
        danger: { background: hov ? "#ff3355" : "transparent", color: COLORS.danger, border: `1px solid ${COLORS.danger}44` },
    };
    return (
        <button
            style={{ ...base, ...variants[variant], ...style }}
            onClick={onClick} disabled={disabled}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        >{children}</button>
    );
}

function Card({ children, onClick, style = {}, hover = true }) {
    const [hov, setHov] = useState(false);
    return (
        <div
            style={{
                background: hov && hover ? COLORS.bgCardHover : COLORS.bgCard,
                border: `1px solid ${hov && hover ? COLORS.accent + "33" : COLORS.border}`,
                borderRadius: 16, padding: 20, transition: "all 0.3s ease",
                cursor: onClick ? "pointer" : "default",
                boxShadow: hov && hover ? `0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px ${COLORS.accent}11` : "0 2px 8px rgba(0,0,0,0.2)",
                ...style,
            }}
            onClick={onClick}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        >{children}</div>
    );
}

function Input({ placeholder, value, onChange, icon, style = {}, ...props }) {
    const [focused, setFocused] = useState(false);
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: COLORS.surface, border: `1px solid ${focused ? COLORS.accent + "66" : COLORS.border}`,
            borderRadius: 12, padding: "12px 16px", transition: "all 0.25s ease",
            boxShadow: focused ? `0 0 0 3px ${COLORS.accent}15` : "none", ...style,
        }}>
            {icon && <span style={{ opacity: 0.6, display: "flex", alignItems: "center" }}>{icon}</span>}
            <input
                style={{
                    background: "transparent", border: "none", outline: "none", flex: 1,
                    color: COLORS.text, fontFamily: FONTS.body, fontSize: 15,
                }}
                placeholder={placeholder} value={value} onChange={onChange}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} {...props}
            />
        </div>
    );
}

function TabBar({ tabs, active, onChange }) {
    return (
        <div style={{ display: "flex", gap: 4, background: COLORS.surface, borderRadius: 14, padding: 4, overflowX: "auto" }}>
            {tabs.map(t => (
                <button key={t.id} onClick={() => onChange(t.id)} style={{
                    padding: "10px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                    fontFamily: FONTS.body, fontSize: 14, fontWeight: active === t.id ? 700 : 500,
                    background: active === t.id ? COLORS.accent : "transparent",
                    color: active === t.id ? COLORS.bg : COLORS.textMuted,
                    transition: "all 0.25s ease", whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 6,
                }}><span style={{ display: "flex" }}>{t.icon}</span> {t.label}</button>
            ))}
        </div>
    );
}

function LureImagePlaceholder({ iconName, size = 48 }) {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={iconName || "fish"} size={size} color={COLORS.accent} />
        </div>
    );
}

// ═══════════════════════════════════════════
// AI SEARCH
// ═══════════════════════════════════════════

function AISearch({ onSelectLure, onCustomOrder }) {
    const [query, setQuery] = useState("");
    const [messages, setMessages] = useState([]);
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [showCustom, setShowCustom] = useState(false);
    const chatRef = useRef(null);

    const suggestions = [
        "I need a crankbait for clear water bass fishing at 6ft depth",
        "Best topwater lure for dawn on a calm lake?",
        "Something for musky in stained water - big profile",
        "Fly fishing trout in mountain streams",
    ];

    const simulateAI = useCallback((q) => {
        setSearching(true);
        const lower = q.toLowerCase();
        setTimeout(() => {
            let matched = MOCK_LURES.filter(l => {
                const searchable = `${l.name} ${l.type} ${l.target} ${l.water} ${l.colors.join(" ")} ${l.artist.specialty}`.toLowerCase();
                const words = lower.split(/\s+/);
                return words.some(w => w.length > 2 && searchable.includes(w));
            });
            if (matched.length === 0) matched = MOCK_LURES.slice(0, 3);

            let response = "";
            if (lower.includes("bass") && lower.includes("clear")) {
                response = `Great question! For clear water bass, you want natural color patterns that mimic local forage. I found ${matched.length} lures that match. The Bluegill Crankbait Pro by Jake Thornton is a top pick - his hand-painted finish is incredibly realistic in clear water. If none of these are exactly what you're after, I can help you design a custom lure.`;
            } else if (lower.includes("musky") || lower.includes("pike")) {
                response = `For musky/pike, you need big profiles with flash and vibration. I found ${matched.length} matches. Dale Hutchins' bucktails are legendary - 50 years of experience on the water. These fish want something they can't ignore.`;
            } else if (lower.includes("fly") || lower.includes("trout")) {
                response = `For fly fishing, Aiko Yamada's artisan flies are exceptional - she blends traditional Japanese techniques with proven Western patterns. I found ${matched.length} options. Each fly is a miniature work of art that actually catches fish.`;
            } else {
                response = `Based on your description, I found ${matched.length} lures that could work well. Take a look below! If nothing's quite right, you can design a custom lure and one of our artists will bring it to life.`;
            }

            setMessages(prev => [...prev, { role: "user", text: q }, { role: "ai", text: response }]);
            setResults(matched.slice(0, 4));
            setSearching(false);
            setQuery("");
        }, 1500);
    }, []);

    useEffect(() => {
        if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }, [messages]);

    return (
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
                <h2 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.text, margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <Icon name="target" size={28} color={COLORS.accent} />
                    <GlowText>AI Lure Finder</GlowText>
                </h2>
                <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 8, fontSize: 15 }}>
                    Describe what you're fishing for and we'll match you with the perfect lure - or help you design one
                </p>
            </div>

            {messages.length === 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 24 }}>
                    {suggestions.map((s, i) => (
                        <button key={i} onClick={() => simulateAI(s)} style={{
                            padding: "8px 16px", borderRadius: 20, border: `1px solid ${COLORS.border}`,
                            background: COLORS.surface, color: COLORS.textMuted, fontFamily: FONTS.body,
                            fontSize: 13, cursor: "pointer", transition: "all 0.2s",
                        }}
                            onMouseEnter={e => { e.target.style.borderColor = COLORS.accent; e.target.style.color = COLORS.accent; }}
                            onMouseLeave={e => { e.target.style.borderColor = COLORS.border; e.target.style.color = COLORS.textMuted; }}
                        >{s}</button>
                    ))}
                </div>
            )}

            {messages.length > 0 && (
                <div ref={chatRef} style={{
                    maxHeight: 300, overflowY: "auto", marginBottom: 20, padding: 16,
                    background: COLORS.surface, borderRadius: 16, border: `1px solid ${COLORS.border}`,
                }}>
                    {messages.map((m, i) => (
                        <div key={i} style={{
                            display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                            marginBottom: 12,
                        }}>
                            <div style={{
                                maxWidth: "80%", padding: "12px 16px", borderRadius: 14,
                                background: m.role === "user" ? COLORS.accent + "22" : COLORS.bgCard,
                                border: `1px solid ${m.role === "user" ? COLORS.accent + "33" : COLORS.border}`,
                                color: COLORS.text, fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.5,
                            }}>
                                {m.role === "ai" && (
                                    <span style={{ color: COLORS.accent, fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                        <Icon name="bot" size={14} color={COLORS.accent} /> LureBot AI
                                    </span>
                                )}
                                {m.text}
                            </div>
                        </div>
                    ))}
                    {searching && (
                        <div style={{ display: "flex", gap: 8, alignItems: "center", color: COLORS.accent, fontFamily: FONTS.body, fontSize: 14 }}>
                            <Icon name="search" size={16} color={COLORS.accent} /> Searching our lure database...
                        </div>
                    )}
                </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
                <Input
                    placeholder="Describe what you're fishing for..."
                    value={query} onChange={e => setQuery(e.target.value)}
                    icon={<Icon name="hook" size={18} color={COLORS.textMuted} />}
                    style={{ flex: 1 }}
                    onKeyDown={e => e.key === "Enter" && query.trim() && simulateAI(query)}
                />
                <Button onClick={() => query.trim() && simulateAI(query)} disabled={!query.trim() || searching}>
                    {searching ? "..." : "Search"}
                </Button>
            </div>

            {results.length > 0 && (
                <div style={{ marginTop: 24 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <h3 style={{ fontFamily: FONTS.body, color: COLORS.text, margin: 0, fontSize: 16, fontWeight: 600 }}>Matched Lures</h3>
                        <button onClick={() => setShowCustom(true)} style={{
                            background: "transparent", border: `1px dashed ${COLORS.accent}66`,
                            color: COLORS.accent, padding: "6px 14px", borderRadius: 8,
                            fontFamily: FONTS.body, fontSize: 13, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 6,
                        }}><Icon name="edit" size={14} color={COLORS.accent} /> None of these? Design your own</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
                        {results.map(lure => (
                            <LureCard key={lure.id} lure={lure} compact onClick={() => onSelectLure?.(lure)} />
                        ))}
                    </div>
                </div>
            )}

            {showCustom && <CustomLureDesigner onClose={() => setShowCustom(false)} onSubmit={onCustomOrder} />}
        </div>
    );
}

// ═══════════════════════════════════════════
// CUSTOM LURE DESIGNER
// ═══════════════════════════════════════════

function CustomLureDesigner({ onClose, onSubmit }) {
    const [step, setStep] = useState(0);
    const [design, setDesign] = useState({ type: "", target: "", water: "", depth: "", colors: "", size: "", action: "", notes: "", budget: "$25-50" });
    const [submitted, setSubmitted] = useState(false);

    const steps = [
        { title: "What type of lure?", field: "type", options: LURE_TYPES },
        { title: "Target species?", field: "target", options: FISH_TARGETS },
        { title: "Water conditions?", field: "water", options: WATER_CONDITIONS },
        { title: "Describe your ideal lure", field: "notes", freeText: true },
    ];

    const matchedArtists = MOCK_ARTISTS.filter(a => {
        if (design.type === "Crankbait" || design.type === "Topwater") return a.id === 1;
        if (design.type === "Swimbait") return a.id === 2;
        if (design.type === "Spinnerbait") return a.id === 3;
        if (design.type === "Fly") return a.id === 4;
        if (design.type === "Jig") return a.id === 5;
        return true;
    }).slice(0, 3);

    if (submitted) {
        return (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                <Card style={{ maxWidth: 500, textAlign: "center", padding: 40 }} hover={false}>
                    <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Icon name="check" size={56} color={COLORS.accent} /></div>
                    <h3 style={{ fontFamily: FONTS.display, color: COLORS.text, fontSize: 24 }}>Custom Order Submitted!</h3>
                    <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, lineHeight: 1.6 }}>Your lure design has been sent to matched artists. You'll receive quotes within 24 hours.</p>
                    <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
                        {matchedArtists.map(a => (
                            <div key={a.id} style={{ textAlign: "center" }}>
                                <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.water})`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.body, fontWeight: 700, color: COLORS.bg, fontSize: 14, margin: "0 auto 6px" }}>{a.avatar}</div>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 11 }}>{a.name.split(" ")[0]}</div>
                            </div>
                        ))}
                    </div>
                    <Button onClick={onClose} style={{ marginTop: 24 }}>Done</Button>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <Card style={{ maxWidth: 560, width: "100%", padding: 32 }} hover={false}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <h3 style={{ fontFamily: FONTS.display, color: COLORS.text, margin: 0, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}>
                        <Icon name="edit" size={22} color={COLORS.accent} /> Design Your Custom Lure
                    </h3>
                    <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 24, cursor: "pointer" }}>x</button>
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
                    {steps.map((_, i) => (<div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? COLORS.accent : COLORS.border, transition: "all 0.3s ease" }} />))}
                </div>
                <h4 style={{ fontFamily: FONTS.body, color: COLORS.text, fontSize: 18, marginBottom: 16, fontWeight: 600 }}>{steps[step].title}</h4>
                {steps[step].freeText ? (
                    <textarea value={design.notes} onChange={e => setDesign({ ...design, notes: e.target.value })} placeholder="Describe colors, patterns, action, size - anything you want the artist to know..." style={{ width: "100%", minHeight: 120, padding: 16, borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, fontFamily: FONTS.body, fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                        {steps[step].options.map(opt => (
                            <button key={opt} onClick={() => setDesign({ ...design, [steps[step].field]: opt })} style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${design[steps[step].field] === opt ? COLORS.accent : COLORS.border}`, background: design[steps[step].field] === opt ? COLORS.accent + "15" : COLORS.surface, color: design[steps[step].field] === opt ? COLORS.accent : COLORS.text, fontFamily: FONTS.body, fontSize: 14, cursor: "pointer", transition: "all 0.2s", fontWeight: design[steps[step].field] === opt ? 600 : 400 }}>{opt}</button>
                        ))}
                    </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28 }}>
                    <Button variant="secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>Back</Button>
                    {step < steps.length - 1 ? (
                        <Button onClick={() => setStep(step + 1)} disabled={!steps[step].freeText && !design[steps[step].field]}>Next</Button>
                    ) : (
                        <Button variant="gold" onClick={() => setSubmitted(true)}><Icon name="send" size={16} color={COLORS.bg} /> Submit to Artists</Button>
                    )}
                </div>
            </Card>
        </div>
    );
}

// ═══════════════════════════════════════════
// LURE CARD
// ═══════════════════════════════════════════

function LureCard({ lure, compact = false, onClick }) {
    return (
        <Card onClick={onClick} style={{ padding: compact ? 14 : 20 }}>
            <div style={{ width: "100%", height: compact ? 100 : 140, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.water}, ${COLORS.waterLight})`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: compact ? 10 : 16 }}>
                <LureImagePlaceholder iconName={lure.iconName} size={compact ? 36 : 48} />
            </div>
            <div style={{ fontFamily: FONTS.body, fontWeight: 700, color: COLORS.text, fontSize: compact ? 13 : 15, marginBottom: 4 }}>{lure.name}</div>
            <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 12, marginBottom: 8 }}>by {lure.artist.name}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontFamily: FONTS.mono, color: COLORS.accent, fontWeight: 700, fontSize: compact ? 16 : 20 }}>${lure.price}</span>
                {!compact && <StarRating rating={lure.rating} />}
            </div>
            {!compact && (
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                    <Badge color={COLORS.accent}>{lure.type}</Badge>
                    <Badge color={COLORS.gold}>{lure.target}</Badge>
                    {!lure.inStock && <Badge color={COLORS.danger}>Sold Out</Badge>}
                </div>
            )}
        </Card>
    );
}

// ═══════════════════════════════════════════
// AUCTION HOUSE
// ═══════════════════════════════════════════

function AuctionHouse() {
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [bidAmount, setBidAmount] = useState("");
    const [bidPlaced, setBidPlaced] = useState(false);

    return (
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
                <h2 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.text, margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <Icon name="flame" size={28} color={COLORS.gold} />
                    <GlowText color={COLORS.gold}>Live Auctions</GlowText>
                </h2>
                <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 8, fontSize: 15 }}>Bid on exclusive, one-of-a-kind handcrafted lures</p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
                {MOCK_AUCTIONS.map(auction => {
                    const isHot = auction.timeLeft.includes("m") && !auction.timeLeft.includes("h");
                    return (
                        <Card key={auction.id} onClick={() => { setSelectedAuction(auction); setBidPlaced(false); setBidAmount(""); }} style={{ position: "relative", overflow: "hidden" }}>
                            {isHot && (
                                <div style={{ position: "absolute", top: 12, right: 12, padding: "4px 10px", background: COLORS.danger, color: "#fff", borderRadius: 8, fontFamily: FONTS.body, fontSize: 11, fontWeight: 700, animation: "pulse 1.5s ease-in-out infinite", display: "flex", alignItems: "center", gap: 4 }}>
                                    <Icon name="flame" size={12} color="#fff" /> ENDING SOON
                                </div>
                            )}
                            <div style={{ width: "100%", height: 160, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.water}88, ${isHot ? COLORS.danger + "33" : COLORS.waterLight}88)`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                                <Icon name={auction.iconName} size={56} color={isHot ? COLORS.gold : COLORS.accent} />
                            </div>
                            <div style={{ fontFamily: FONTS.body, fontWeight: 700, color: COLORS.text, fontSize: 16, marginBottom: 4 }}>{auction.name}</div>
                            <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>by {auction.artist.name} | {auction.bids} bids</div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", padding: "14px 0 0", borderTop: `1px solid ${COLORS.border}` }}>
                                <div>
                                    <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Current Bid</div>
                                    <div style={{ fontFamily: FONTS.mono, color: COLORS.gold, fontWeight: 700, fontSize: 26 }}>${auction.currentBid}</div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Time Left</div>
                                    <div style={{ fontFamily: FONTS.mono, color: isHot ? COLORS.danger : COLORS.text, fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                                        <Icon name="clock" size={16} color={isHot ? COLORS.danger : COLORS.text} /> {auction.timeLeft}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {selectedAuction && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setSelectedAuction(null)}>
                    <Card style={{ maxWidth: 600, width: "100%", padding: 32 }} hover={false} onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                            <div>
                                <h3 style={{ fontFamily: FONTS.display, color: COLORS.text, margin: 0, fontSize: 22 }}>{selectedAuction.name}</h3>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 14, marginTop: 4 }}>by {selectedAuction.artist.name} | {selectedAuction.artist.location}</div>
                            </div>
                            <button onClick={() => setSelectedAuction(null)} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 28, cursor: "pointer" }}>x</button>
                        </div>
                        <div style={{ width: "100%", height: 200, borderRadius: 14, marginTop: 20, background: `linear-gradient(135deg, ${COLORS.water}, ${COLORS.waterLight})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Icon name={selectedAuction.iconName} size={72} color={COLORS.accent} />
                        </div>
                        <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 14, lineHeight: 1.6, marginTop: 16 }}>{selectedAuction.description}</p>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 20, padding: 16, background: COLORS.surface, borderRadius: 12 }}>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase" }}>Current Bid</div>
                                <div style={{ fontFamily: FONTS.mono, color: COLORS.gold, fontWeight: 700, fontSize: 24 }}>${selectedAuction.currentBid}</div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase" }}>Total Bids</div>
                                <div style={{ fontFamily: FONTS.mono, color: COLORS.text, fontWeight: 700, fontSize: 24 }}>{selectedAuction.bids}</div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase" }}>Time Left</div>
                                <div style={{ fontFamily: FONTS.mono, color: COLORS.danger, fontWeight: 700, fontSize: 24 }}>{selectedAuction.timeLeft}</div>
                            </div>
                        </div>
                        {bidPlaced ? (
                            <div style={{ marginTop: 20, padding: 20, background: COLORS.accent + "15", borderRadius: 12, border: `1px solid ${COLORS.accent}33`, textAlign: "center" }}>
                                <div style={{ display: "flex", justifyContent: "center" }}><Icon name="check" size={32} color={COLORS.accent} /></div>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.accent, fontWeight: 700, fontSize: 16, marginTop: 8 }}>Bid Placed - ${bidAmount}</div>
                                <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 13, marginTop: 4 }}>You'll be notified if you're outbid</div>
                            </div>
                        ) : (
                            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                                <Input placeholder={`Min bid: $${selectedAuction.currentBid + 5}`} value={bidAmount} onChange={e => setBidAmount(e.target.value.replace(/[^0-9]/g, ""))} icon={<Icon name="dollar" size={18} color={COLORS.textMuted} />} style={{ flex: 1 }} />
                                <Button variant="gold" onClick={() => { if (Number(bidAmount) > selectedAuction.currentBid) setBidPlaced(true); }} disabled={!bidAmount || Number(bidAmount) <= selectedAuction.currentBid}>Place Bid</Button>
                            </div>
                        )}
                    </Card>
                </div>
            )}

            <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }`}</style>
        </div>
    );
}

// ═══════════════════════════════════════════
// ARTIST PAGES
// ═══════════════════════════════════════════

function ArtistPages() {
    const [selectedArtist, setSelectedArtist] = useState(null);

    return (
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
                <h2 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.text, margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <Icon name="palette" size={28} color={COLORS.accent} />
                    <GlowText>Master Craftsmen</GlowText>
                </h2>
                <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 8, fontSize: 15 }}>Every artist page is professionally curated - quality guaranteed</p>
            </div>

            {selectedArtist ? (
                <ArtistProfile artist={selectedArtist} onBack={() => setSelectedArtist(null)} />
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 }}>
                    {MOCK_ARTISTS.map(artist => (
                        <Card key={artist.id} onClick={() => setSelectedArtist(artist)}>
                            <div style={{ display: "flex", gap: 16, alignItems: "start" }}>
                                <div style={{ width: 64, height: 64, borderRadius: 16, flexShrink: 0, background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.waterLight})`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.body, fontWeight: 800, color: COLORS.bg, fontSize: 20, boxShadow: `0 4px 16px ${COLORS.accent}33` }}>{artist.avatar}</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontFamily: FONTS.body, fontWeight: 700, color: COLORS.text, fontSize: 17 }}>{artist.name}</span>
                                        {artist.verified && <Badge color={COLORS.accent} style={{ padding: "2px 8px", fontSize: 10 }}>Verified</Badge>}
                                    </div>
                                    <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 13, marginTop: 2 }}>{artist.handle} | {artist.location}</div>
                                </div>
                            </div>
                            <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 14, marginTop: 14, padding: "14px 0", borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, lineHeight: 1.5 }}>
                                <Badge color={COLORS.gold} style={{ marginBottom: 8 }}>{artist.specialty}</Badge>
                                <p style={{ margin: "8px 0 0" }}>{artist.bio}</p>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, paddingTop: 2 }}>
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontFamily: FONTS.mono, color: COLORS.accent, fontWeight: 700, fontSize: 18 }}>{artist.sales.toLocaleString()}</div>
                                    <div style={{ fontFamily: FONTS.body, color: COLORS.textDim, fontSize: 11 }}>Sales</div>
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <StarRating rating={artist.rating} />
                                    <div style={{ fontFamily: FONTS.body, color: COLORS.textDim, fontSize: 11 }}>{artist.reviews} reviews</div>
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontFamily: FONTS.mono, color: COLORS.text, fontWeight: 700, fontSize: 16 }}>{artist.responseTime}</div>
                                    <div style={{ fontFamily: FONTS.body, color: COLORS.textDim, fontSize: 11 }}>Response</div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

function ArtistProfile({ artist, onBack }) {
    const artistLures = MOCK_LURES.filter(l => l.artist.id === artist.id);
    const artistAuctions = MOCK_AUCTIONS.filter(a => a.artist.id === artist.id);

    return (
        <div>
            <button onClick={onBack} style={{ background: "none", border: "none", color: COLORS.accent, fontFamily: FONTS.body, fontSize: 14, cursor: "pointer", marginBottom: 20, display: "flex", alignItems: "center", gap: 6 }}>{"<-"} Back to Artists</button>

            <div style={{ borderRadius: 20, overflow: "hidden", marginBottom: 24, background: `linear-gradient(135deg, ${COLORS.water}cc, ${COLORS.bg} 60%)`, border: `1px solid ${COLORS.border}`, padding: 32 }}>
                <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ width: 100, height: 100, borderRadius: 24, background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.waterLight})`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.body, fontWeight: 800, color: COLORS.bg, fontSize: 32, boxShadow: `0 8px 32px ${COLORS.accent}33` }}>{artist.avatar}</div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <h2 style={{ fontFamily: FONTS.display, color: COLORS.text, margin: 0, fontSize: 28 }}>{artist.name}</h2>
                            <Badge color={COLORS.accent}>Verified Artist</Badge>
                        </div>
                        <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 4, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                            <Icon name="pin" size={14} color={COLORS.textMuted} /> {artist.location} | Member since {artist.joined} | Responds in {artist.responseTime}
                        </div>
                        <p style={{ fontFamily: FONTS.body, color: COLORS.text, marginTop: 10, fontSize: 15, lineHeight: 1.6, maxWidth: 500 }}>{artist.bio}</p>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                        <Button><Icon name="chat" size={16} color={COLORS.bg} /> Message</Button>
                        <Button variant="gold"><Icon name="star" size={16} color={COLORS.bg} /> Follow</Button>
                    </div>
                </div>
                <div style={{ display: "flex", gap: 24, marginTop: 24, paddingTop: 20, borderTop: `1px solid ${COLORS.border}`, flexWrap: "wrap" }}>
                    {[
                        { label: "Total Sales", value: artist.sales.toLocaleString(), color: COLORS.accent },
                        { label: "Reviews", value: artist.reviews, color: COLORS.text },
                        { label: "Rating", value: `${String.fromCharCode(9733)} ${artist.rating}`, color: COLORS.gold },
                        { label: "Specialty", value: artist.specialty, color: COLORS.text },
                    ].map(s => (
                        <div key={s.label}>
                            <div style={{ fontFamily: FONTS.body, color: COLORS.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
                            <div style={{ fontFamily: FONTS.mono, color: s.color, fontWeight: 700, fontSize: 18, marginTop: 4 }}>{s.value}</div>
                        </div>
                    ))}
                </div>
            </div>

            <Card style={{ padding: 16, marginBottom: 24, display: "flex", alignItems: "center", gap: 14, background: COLORS.accent + "08" }} hover={false}>
                <Icon name="camera" size={28} color={COLORS.accent} />
                <div>
                    <div style={{ fontFamily: FONTS.body, fontWeight: 700, color: COLORS.accent, fontSize: 14 }}>AI-Enhanced Photos</div>
                    <div style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 13 }}>All product photos are professionally enhanced - consistent lighting, clean backgrounds, and true-to-life colors guaranteed.</div>
                </div>
            </Card>

            <h3 style={{ fontFamily: FONTS.display, color: COLORS.text, fontSize: 20, marginBottom: 16 }}>Available Lures</h3>
            {artistLures.length > 0 ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
                    {artistLures.map(lure => <LureCard key={lure.id} lure={lure} />)}
                </div>
            ) : (
                <Card hover={false} style={{ textAlign: "center", padding: 32 }}>
                    <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted }}>No lures currently listed. Request a custom order!</p>
                    <Button style={{ marginTop: 12 }}><Icon name="edit" size={16} color={COLORS.bg} /> Request Custom Lure</Button>
                </Card>
            )}

            {artistAuctions.length > 0 && (
                <>
                    <h3 style={{ fontFamily: FONTS.display, color: COLORS.text, fontSize: 20, marginTop: 32, marginBottom: 16 }}>Active Auctions</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
                        {artistAuctions.map(a => (
                            <Card key={a.id} style={{ padding: 16 }}>
                                <div style={{ fontFamily: FONTS.body, fontWeight: 700, color: COLORS.text, fontSize: 15 }}>{a.name}</div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, alignItems: "center" }}>
                                    <span style={{ fontFamily: FONTS.mono, color: COLORS.gold, fontWeight: 700 }}>${a.currentBid}</span>
                                    <span style={{ fontFamily: FONTS.mono, color: COLORS.danger, fontSize: 14, display: "flex", alignItems: "center", gap: 4 }}>
                                        <Icon name="clock" size={14} color={COLORS.danger} /> {a.timeLeft}
                                    </span>
                                </div>
                            </Card>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════
// BROWSE / SHOP
// ═══════════════════════════════════════════

function Shop() {
    const [typeFilter, setTypeFilter] = useState("All");
    const [targetFilter, setTargetFilter] = useState("All");
    const filtered = MOCK_LURES.filter(l => (typeFilter === "All" || l.type === typeFilter) && (targetFilter === "All" || l.target === targetFilter));

    return (
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
                <h2 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.text, margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <Icon name="cart" size={28} color={COLORS.accent} />
                    <GlowText>Shop Lures</GlowText>
                </h2>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24, justifyContent: "center" }}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 13, marginRight: 4 }}>Type:</span>
                    {["All", ...LURE_TYPES.slice(0, 5)].map(t => (
                        <button key={t} onClick={() => setTypeFilter(t)} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${typeFilter === t ? COLORS.accent : COLORS.border}`, background: typeFilter === t ? COLORS.accent + "18" : "transparent", color: typeFilter === t ? COLORS.accent : COLORS.textMuted, fontFamily: FONTS.body, fontSize: 12, cursor: "pointer" }}>{t}</button>
                    ))}
                </div>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span style={{ fontFamily: FONTS.body, color: COLORS.textMuted, fontSize: 13, marginRight: 4 }}>Target:</span>
                    {["All", ...FISH_TARGETS.slice(0, 4)].map(t => (
                        <button key={t} onClick={() => setTargetFilter(t)} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${targetFilter === t ? COLORS.gold : COLORS.border}`, background: targetFilter === t ? COLORS.gold + "18" : "transparent", color: targetFilter === t ? COLORS.gold : COLORS.textMuted, fontFamily: FONTS.body, fontSize: 12, cursor: "pointer" }}>{t}</button>
                    ))}
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 18 }}>
                {filtered.map(lure => <LureCard key={lure.id} lure={lure} />)}
            </div>
            {filtered.length === 0 && (
                <Card hover={false} style={{ textAlign: "center", padding: 40 }}>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Icon name="search" size={48} color={COLORS.textDim} /></div>
                    <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 12 }}>No lures match those filters. Try a different combination or use AI Search to find what you need.</p>
                </Card>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════

const NAV_TABS = [
    { id: "search", label: "AI Search", icon: <Icon name="target" size={16} /> },
    { id: "shop", label: "Shop", icon: <Icon name="cart" size={16} /> },
    { id: "auctions", label: "Auctions", icon: <Icon name="flame" size={16} /> },
    { id: "artists", label: "Artists", icon: <Icon name="palette" size={16} /> },
];

export default function LureLink() {
    const [activeTab, setActiveTab] = useState("search");
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const link1 = document.createElement("link");
        link1.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap";
        link1.rel = "stylesheet";
        document.head.appendChild(link1);
        setTimeout(() => setLoaded(true), 100);
    }, []);

    return (
        <div style={{ minHeight: "100vh", background: COLORS.bg, color: COLORS.text, fontFamily: FONTS.body, opacity: loaded ? 1 : 0, transition: "opacity 0.6s ease" }}>
            <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, background: `radial-gradient(ellipse 80% 50% at 50% -10%, ${COLORS.water}44, transparent), radial-gradient(ellipse 60% 30% at 80% 100%, ${COLORS.accent}08, transparent)` }} />

            <header style={{ position: "sticky", top: 0, zIndex: 100, background: COLORS.bg + "ee", backdropFilter: "blur(16px)", borderBottom: `1px solid ${COLORS.border}`, padding: "14px 24px" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Icon name="hook" size={28} color={COLORS.accent} />
                        <span style={{ fontFamily: FONTS.display, fontSize: 24, fontWeight: 800, background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.gold})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: -0.5 }}>LureLink</span>
                        <Badge color={COLORS.textDim} style={{ fontSize: 9, padding: "2px 6px" }}>BETA</Badge>
                    </div>
                    <TabBar tabs={NAV_TABS} active={activeTab} onChange={setActiveTab} />
                    <div style={{ display: "flex", gap: 10 }}>
                        <Button variant="secondary" style={{ padding: "8px 14px", fontSize: 13 }}>Sign In</Button>
                        <Button style={{ padding: "8px 14px", fontSize: 13 }}>Join Free</Button>
                    </div>
                </div>
            </header>

            <main style={{ position: "relative", zIndex: 1, padding: "32px 24px 64px", maxWidth: 1100, margin: "0 auto" }}>
                {activeTab === "search" && <AISearch />}
                {activeTab === "shop" && <Shop />}
                {activeTab === "auctions" && <AuctionHouse />}
                {activeTab === "artists" && <ArtistPages />}
            </main>

            <footer style={{ borderTop: `1px solid ${COLORS.border}`, padding: "32px 24px", textAlign: "center", position: "relative", zIndex: 1 }}>
                <div style={{ fontFamily: FONTS.body, color: COLORS.textDim, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <Icon name="hook" size={16} color={COLORS.textDim} /> LureLink - Connecting anglers with master lure craftsmen
                </div>
                <div style={{ fontFamily: FONTS.body, color: COLORS.textDim, fontSize: 11, marginTop: 6 }}>
                    {String.fromCharCode(169)} 2026 LureLink | All photos AI-enhanced for professional quality
                </div>
            </footer>
        </div>
    );
}